import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Delete, ContentCopy, Visibility, VisibilityOff, Restore } from '@mui/icons-material';
import { formatApiKeyUsage } from '../../utils/helpers';

const ApiKeysList = ({ apiKeys, onDeactivate, onRestore, onShowKey, onCopyKey, showApiKeys, onNavigate }) => {
    const activeApiKeys = apiKeys.filter(key => key.is_active);
    const deactivatedApiKeys = apiKeys.filter(key => !key.is_active);

    return (
        <Box sx={{ mt: 4 }} data-test-id="api-keys-list">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" gutterBottom>
                API Keys ({activeApiKeys.length})
                </Typography>
                <Button
                variant="contained"
                color="primary"
                onClick={() => onNavigate('/create-key')}
                data-test-id="api-keys-create-btn"
                >
                Create New Key
                </Button>
            </Box>
            
            {/* API Keys Usage Summary */}
            {apiKeys && apiKeys.length > 0 && (
                <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Usage Summary
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 2 }}>
                        <Typography variant="h6" color="primary">
                            {apiKeys.reduce((sum, key) => sum + (key.total_requests || 0), 0)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Total Requests
                        </Typography>
                        </CardContent>
                    </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 2 }}>
                        <Typography variant="h6" color="primary">
                            {apiKeys.reduce((sum, key) => sum + (key.total_duration_minutes || 0), 0).toFixed(1)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Total Minutes Used
                        </Typography>
                        </CardContent>
                    </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 2 }}>
                        <Typography variant="h6" color="primary">
                            {activeApiKeys.length}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Active Keys
                        </Typography>
                        </CardContent>
                    </Card>
                    </Grid>
                </Grid>
                </Box>
            )}
            <Grid container spacing={2}>
                {activeApiKeys
                    ?.sort((a, b) => (b.total_duration_minutes || 0) - (a.total_duration_minutes || 0))
                    .map((apiKey) => (
                <Grid item xs={12} md={6} key={apiKey.id}>
                    <Card data-test-id={`api-key-card-${apiKey.id}`}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" gutterBottom>
                                {apiKey.name}
                            </Typography>
                            {apiKey.total_duration_minutes > 0 && (
                                <Chip 
                                label="Most Used" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ 
                                    display: apiKey.total_duration_minutes === Math.max(...apiKeys.map(k => k.total_duration_minutes || 0)) ? 'flex' : 'none'
                                }}
                                />
                            )}
                            </Box>
                            <Typography color="textSecondary" variant="body2">
                            Created: {new Date(apiKey.created_at).toLocaleDateString()}
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                            Status: {apiKey.is_active ? 'Active' : 'Inactive'}
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                            Requests: {apiKey.total_requests || 0}
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                            Usage: {formatApiKeyUsage(apiKey.total_duration_minutes || 0)}
                            </Typography>
                            {apiKey.last_used && (
                            <Typography color="textSecondary" variant="body2">
                                Last used: {new Date(apiKey.last_used).toLocaleDateString()}
                            </Typography>
                            )}
                            {apiKey.key && (
                            <Box sx={{ mt: 1 }}>
                                <Typography color="textSecondary" variant="body2" gutterBottom>
                                API Key:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                    fontFamily: 'monospace', 
                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                                    color: (theme) => theme.palette.mode === 'dark' ? 'text.primary' : 'text.primary',
                                    p: 1, 
                                    borderRadius: 1,
                                    flex: 1,
                                    wordBreak: 'break-all'
                                    }}
                                >
                                    {showApiKeys[apiKey.id] ? apiKey.key : '••••••••••••••••'}
                                </Typography>
                                <Tooltip title={showApiKeys[apiKey.id] ? 'Hide' : 'Show'}>
                                    <IconButton
                                    size="small"
                                    onClick={() => onShowKey(apiKey.id)}
                                    data-test-id={`api-key-toggle-visibility-${apiKey.id}`}
                                    >
                                    {showApiKeys[apiKey.id] ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Copy to clipboard">
                                    <IconButton
                                    size="small"
                                    onClick={() => onCopyKey(apiKey)}
                                    data-test-id={`api-key-copy-${apiKey.id}`}
                                    >
                                    <ContentCopy />
                                    </IconButton>
                                </Tooltip>
                                </Box>
                            </Box>
                            )}
                        </Box>
                        <Tooltip title="Deactivate API key">
                            <IconButton
                            color="warning"
                            size="small"
                            onClick={() => onDeactivate(apiKey)}
                            data-test-id={`api-key-deactivate-${apiKey.id}`}
                            >
                            <Delete />
                            </IconButton>
                        </Tooltip>
                        </Box>
                    </CardContent>
                    </Card>
                </Grid>
                ))}
                {activeApiKeys.length === 0 && (
                <Grid item xs={12}>
                    <Card>
                    <CardContent>
                        <Typography color="textSecondary">
                        No active API keys created yet. Create your first API key to start using the service.
                        </Typography>
                    </CardContent>
                    </Card>
                </Grid>
                )}
            </Grid>

            {deactivatedApiKeys.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom color="textSecondary">
                    Deactivated API Keys ({deactivatedApiKeys.length})
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    These API keys have been deactivated but can be restored if needed.
                    </Typography>
                    <Grid container spacing={2}>
                    {deactivatedApiKeys
                        .map((apiKey) => (
                        <Grid item xs={12} md={6} key={apiKey.id}>
                            <Card variant="outlined" sx={{ opacity: 0.7 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box flex={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h6" gutterBottom color="textSecondary">
                                        {apiKey.name}
                                    </Typography>
                                    <Chip 
                                        label="Deactivated" 
                                        size="small" 
                                        color="error" 
                                        variant="outlined"
                                    />
                                    </Box>
                                    <Typography color="textSecondary" variant="body2">
                                    Created: {new Date(apiKey.created_at).toLocaleDateString()}
                                    </Typography>
                                    {apiKey.deleted_at && (
                                    <Typography color="textSecondary" variant="body2">
                                        Deactivated: {new Date(apiKey.deleted_at).toLocaleDateString()}
                                    </Typography>
                                    )}
                                    <Typography color="textSecondary" variant="body2">
                                    Requests: {apiKey.total_requests || 0}
                                    </Typography>
                                    <Typography color="textSecondary" variant="body2">
                                    Usage: {formatApiKeyUsage(apiKey.total_duration_minutes || 0)}
                                    </Typography>
                                    {apiKey.last_used && (
                                    <Typography color="textSecondary" variant="body2">
                                        Last used: {new Date(apiKey.last_used).toLocaleDateString()}
                                    </Typography>
                                    )}
                                </Box>
                                <Tooltip title="Restore API key">
                                    <IconButton
                                    color="success"
                                    size="small"
                                    onClick={() => onRestore(apiKey.id)}
                                    data-test-id={`api-key-restore-${apiKey.id}`}
                                    >
                                    <Restore />
                                    </IconButton>
                                </Tooltip>
                                </Box>
                            </CardContent>
                            </Card>
                        </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
}

export default ApiKeysList;
